<script lang="ts" setup>
import GiftASticker from '@/views/manage-content/media/stickers/GiftASticker.vue';
import SweepstakesWinnersPanel from '@/components/SweepstakesWinnersPanel.vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import shared from '@/views/manage-content/media/shared';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(lint): <sticker-insights> resolves to the `stickerInsights` object, not this component
import StickerInsights from '@/views/manage-content/media/stickers/StickerInsights.vue';
import type { SweepstakesPanelData } from '@/components/SweepstakesWinnersPanel.vue';

const route = useRoute();
const store = useStore();
const loading = ref(false);
const {
  findStickerInsights,
  getWinners,
  getSticker,
  getSweepstakesLifecycleData,
} = shared();

const item = computed(() => store.getters['sticker/GET_STICKER']);

const stickerId = computed(() => (
  (route.params.id as string | undefined) || item.value?.id || null
));

const stickerInsights = reactive({});
const winners = ref<Winner[]>([]);
const sweepstakesData = ref<SweepstakesPanelData | null>(null);

interface Winner {
  id: string;
  stickerId: string;
  entityId: string;
  participantName: string;
  participantAvatar: string;
  participantEmail: string;
  eligible: boolean;
  notifiedAt?: string;
  dateCreated: string;
  dateUpdated: string;
}

const isSweepstakes = computed(() => item.value?.campaignType === 'Sweepstakes');

async function getStickerInsights() {
  if (!stickerId.value) {
    return;
  }
  loading.value = true;
  try {
    const mStickerInsights = await findStickerInsights(stickerId.value);
    Object.assign(stickerInsights, mStickerInsights ?? {});
  } catch (error) {
    console.error('Error fetching sticker insights:', error);
  } finally {
    loading.value = false;
  }
}

async function getWinnersList() {
  if (item.value?.campaignType !== 'Sweepstakes' || !item.value?.id) {
    winners.value = [];
    return;
  }
  try {
    const response = await getWinners(item.value.id);
    winners.value = response ?? [];
  } catch (error) {
    console.error('Error fetching winners:', error);
  }
}

// The sticker payload's sweepstakesSticker has no drawnAt / losersNotifiedAt, and
// getStickerAnalytics refuses platform admins, so read the sweepstakes row directly.
async function getSweepstakesPanelData() {
  if (!isSweepstakes.value || !item.value?.id) return;

  try {
    const sticker = (await getSticker(item.value.id)) || item.value;
    const lifecycle = sticker.sweepstakesStickerId
      ? await getSweepstakesLifecycleData(sticker.sweepstakesStickerId)
      : null;
    const sweepstakes = lifecycle || sticker.sweepstakesSticker;

    sweepstakesData.value = {
      status: sticker.status,
      isWinnerConfirmed: sticker.isWinnerConfirmed,
      drawingDateTime: sweepstakes?.drawingDateTime,
      drawingDateTimeTimezone: sweepstakes?.drawingDateTimeTimezone,
      drawnAt: sweepstakes?.drawnAt,
      losersNotifiedAt: sweepstakes?.losersNotifiedAt,
      numberOfWinners: sweepstakes?.numberOfPrizesAndWinners,
    };

    // Keep the edit form in step: the draw archives the sticker and confirm closes it.
    const current = item.value;
    if (current && current !== sticker) {
      current.status = sticker.status;
      current.isWinnerConfirmed = sticker.isWinnerConfirmed;
    }
  } catch (error) {
    console.error('Error fetching sweepstakes lifecycle:', error);
  }
}

async function refreshSweepstakesData() {
  await Promise.all([
    getWinnersList(),
    getSweepstakesPanelData(),
  ]);
}

async function loadInsightsData() {
  await getStickerInsights();
  if (isSweepstakes.value) {
    await refreshSweepstakesData();
  } else {
    await getWinnersList();
  }
}

onMounted(loadInsightsData);

watch(stickerId, (id, prev) => {
  if (id && id !== prev) {
    loadInsightsData();
  }
});
</script>

<template>
  <div class="c-add-sticker__extra__type c-add-sticker__insights">
    <div v-if="loading" class="c-add-sticker__insights-loading">
      Loading insights...
    </div>
    <template v-else>
      <sticker-insights
        inline
        :sticker-insights="stickerInsights"
        :winners="winners"
        :hide-winners="isSweepstakes"
      />

      <section
        v-if="isSweepstakes"
        class="c-add-sticker__sweepstakes-panel"
      >
        <SweepstakesWinnersPanel
          :sticker-id="item.id"
          :sweepstakes="sweepstakesData"
          :cms-winners="winners"
          @refreshed="refreshSweepstakesData"
        />
      </section>

      <gift-a-sticker v-if="item.campaignType === 'Ticket'" :stickerId="stickerId" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.c-add-sticker__insights-loading {
  font-size: 12px;
  color: #666;
}

.c-add-sticker__sweepstakes-panel {
  padding: 20px 0 0;
  margin: 20px 0 0;
  border-top: 1px solid #efefef;
}
</style>
